import {Component, computed, inject, input, model, output, Signal} from '@angular/core';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {VulcanIconComponent} from '@eagle-i/vulcan-component-library';
import {MatSlider, MatSliderThumb} from '@angular/material/slider';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {debounceTime, filter, map} from 'rxjs';
import BaseLayer from 'ol/layer/Base';
import {BaseUILayer} from '../../classes/layers/base-ui-layer';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {InformationModalComponent} from '../../modals/information-modal/information-modal.component';
import {MapLayer} from '../../classes/map-layer';
import {MapService} from '../../../services/map.service';


interface LayerActions {
  info?: boolean;
  download?: boolean;
  identify?: boolean;
}

@Component({
  selector: 'tgrid-layer-header',
  standalone: true,
  imports: [
    MatSlideToggle,
    ReactiveFormsModule,
    VulcanIconComponent,
    MatSlider,
    MatSliderThumb,
  ],
  templateUrl: './layer-header.component.html',
  styleUrl: './layer-header.component.scss',
})
export class LayerHeaderComponent {

  private dialog = inject(MatDialog);

  public action = output<string>();

  public isActive = model(false);
  public opacity = model<number>(80);

  public layer = input.required<BaseUILayer<BaseLayer>>();

  protected mapLayer: Signal<MapLayer<BaseLayer>> = computed(() => this.layer().mapLayer());

  public additionIcons = input<LayerActions>({
    info: false,
    download: false,
    identify: false,
  });

  protected isIdentifiable: Signal<boolean> = toSignal(inject(MapService).activeIdentifyLayers.pipe(
      map((layers) => {
        return layers.some(layer => layer.uiHandle === this.mapLayer().uiHandle);
      }),
    ), {initialValue: false},
  );

  protected activeControl: FormControl = new FormControl<boolean>(this.isActive());
  protected opacityControl: FormControl = new FormControl<number>(this.opacity());

  constructor() {
    this.opacityControl.valueChanges
      .pipe(
        debounceTime(100),
        filter(() => this.activeControl.value),
        takeUntilDestroyed(),
      )
      .subscribe(value => {
        this.layer().changeOpacity(value);
      });


    this.activeControl.valueChanges.pipe(
      takeUntilDestroyed(),
    ).subscribe(value => {
      this.layer()?.toggleLayer(value);
    });
  }


  protected emitAction(event: MouseEvent, action: string): void {
    this.action.emit(action);
  }

  public formatLabel(value: number): string {
    return `${value}%`;
  }

  protected openLayerInfoModal(event: MouseEvent) {
    event.stopPropagation();

    const config: MatDialogConfig = {
      width: '500px',
      autoFocus: false,
      data: {
        title: `${this.mapLayer().displayName} Layer Info`,
        infoText: this.mapLayer().description,
      },
    };

    this.dialog.open(InformationModalComponent, config);

  }

}
